/**
 * ToolboxProfileContext (#638).
 *
 * Loads the current user's toolbox profiles (their own + the global default),
 * holds the active profile, and exposes the resolved (permission-gated, ordered)
 * tool list for the toolbox to render. Mutations delegate to the Phase 1 client
 * (`toolboxProfileService`) and refresh state from the server.
 *
 * The catalogue is supplied as a prop (mirroring `resolveActiveTools`, which
 * takes the catalogue as a parameter) so the context stays decoupled from the
 * `TOOL_KEYS` / `navigationConfig` wiring that lives in ToolBox.
 *
 * Fail-fast: a failed load surfaces via `error` and leaves no active profile —
 * it does not silently fall back to an empty toolbox.
 */

import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { toolboxProfileService } from '../services/toolboxProfileService';
import { usePermissions } from './PermissionContext';
import {
  resolveActiveTools,
  type CatalogueTool,
} from '../easymap/mapcontrols/ToolBox/toolboxCatalogue';
import {
  USER_DEFAULT_NAME,
  type ToolboxProfile,
  type CreateToolboxProfileInput,
  type UpdateToolboxProfileInput,
} from '../types/toolboxProfile';

interface ToolboxProfileContextValue {
  /** All profiles available to the user (own profiles + the global default). */
  profiles: ToolboxProfile[];
  /** The currently selected profile, or null before load / after a failed load. */
  activeProfile: ToolboxProfile | null;
  /** Active profile's tools, in profile order, filtered to those the user can use. */
  resolvedTools: CatalogueTool[];
  /** True while the initial load is in progress. */
  loading: boolean;
  /** Set when the load failed (fail-fast — no silent empty state). */
  error: Error | null;
  /** Select a profile as active by id. */
  setActiveProfile: (id: string) => void;
  /** Create a profile, refresh, and make the new profile active. */
  save: (input: CreateToolboxProfileInput) => Promise<ToolboxProfile>;
  /** Update (rename / reorder / re-enable) a profile and refresh. */
  update: (id: string, input: UpdateToolboxProfileInput) => Promise<ToolboxProfile>;
  /** Delete a profile and refresh; if the active one was removed, fall back to the global default. */
  remove: (id: string) => Promise<void>;
}

const ToolboxProfileContext = createContext<ToolboxProfileContextValue | null>(null);

/**
 * Pick the initial active profile: the user's permanent 'User Default' (always
 * loaded first), else the global default, else the first available.
 */
function pickDefaultActiveId(profiles: ToolboxProfile[]): string | null {
  const userDefault = profiles.find((p) => !p.is_global && p.name === USER_DEFAULT_NAME);
  if (userDefault) return userDefault.id;
  const globalDefault = profiles.find((p) => p.is_global);
  if (globalDefault) return globalDefault.id;
  return profiles[0]?.id ?? null;
}

export const ToolboxProfileProvider: React.FC<{
  catalogue: CatalogueTool[];
  children: React.ReactNode;
}> = ({ catalogue, children }) => {
  const { canDo } = usePermissions();
  const [profiles, setProfiles] = useState<ToolboxProfile[]>([]);
  const [activeProfileId, setActiveProfileId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const loaded = await toolboxProfileService.getProfilesForCurrentUser();
        if (cancelled) return;
        setProfiles(loaded);
        setActiveProfileId(pickDefaultActiveId(loaded));
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setProfiles([]);
        setActiveProfileId(null);
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  /** Re-fetch profiles from the server; returns the fresh list. */
  const refresh = useCallback(async (): Promise<ToolboxProfile[]> => {
    const loaded = await toolboxProfileService.getProfilesForCurrentUser();
    setProfiles(loaded);
    return loaded;
  }, []);

  const setActiveProfile = useCallback((id: string) => {
    setActiveProfileId(id);
  }, []);

  const save = useCallback(
    async (input: CreateToolboxProfileInput): Promise<ToolboxProfile> => {
      const created = await toolboxProfileService.createProfile(input);
      await refresh();
      setActiveProfileId(created.id);
      return created;
    },
    [refresh],
  );

  const update = useCallback(
    async (id: string, input: UpdateToolboxProfileInput): Promise<ToolboxProfile> => {
      const updated = await toolboxProfileService.updateProfile(id, input);
      await refresh();
      return updated;
    },
    [refresh],
  );

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await toolboxProfileService.deleteProfile(id);
      const fresh = await refresh();
      setActiveProfileId((current) =>
        current === id ? pickDefaultActiveId(fresh) : current,
      );
    },
    [refresh],
  );

  const activeProfile = useMemo(
    () => profiles.find((p) => p.id === activeProfileId) ?? null,
    [profiles, activeProfileId],
  );

  const resolvedTools = useMemo(
    () =>
      activeProfile
        ? resolveActiveTools(activeProfile.profile_data.tools, catalogue, canDo)
        : [],
    [activeProfile, catalogue, canDo],
  );

  const value = useMemo<ToolboxProfileContextValue>(
    () => ({
      profiles,
      activeProfile,
      resolvedTools,
      loading,
      error,
      setActiveProfile,
      save,
      update,
      remove,
    }),
    [profiles, activeProfile, resolvedTools, loading, error, setActiveProfile, save, update, remove],
  );

  return (
    <ToolboxProfileContext.Provider value={value}>{children}</ToolboxProfileContext.Provider>
  );
};

/**
 * Access the toolbox profile state.
 *
 * Must be called inside <ToolboxProfileProvider>.
 */
export function useToolboxProfile(): ToolboxProfileContextValue {
  const ctx = useContext(ToolboxProfileContext);
  if (!ctx) {
    throw new Error('useToolboxProfile must be used within a ToolboxProfileProvider');
  }
  return ctx;
}

/**
 * Optional access to the toolbox profile state.
 *
 * Returns null instead of throwing when used outside <ToolboxProfileProvider>,
 * so a component (e.g. ToolBox) can degrade gracefully to legacy behaviour
 * until the provider is mounted.
 */
export function useOptionalToolboxProfile(): ToolboxProfileContextValue | null {
  return useContext(ToolboxProfileContext);
}
